import { LiveQuestRewardSlot } from './liveQuestRewards';
import { inspectProfileFrameFile } from '../utils/profileFrameRules';
import { inspectAnimatedBackgroundFile } from '../utils/profileBackgroundRules';

/**
 * What artwork each kind of Live Quest reward needs. The rules mirror the rest of the console
 * (profile frames, animated frames and entry animations in the product form) so one asset
 * satisfies both places.
 *
 * Stored on the reward's product as `MediaAssets`:
 *   image      static artwork (also the card icon in the app)
 *   thumbnail  WebP still of an animation (the card icon, and what old app versions show)
 *   webm       Android animation
 *   ios_alpha  iOS .mov animation (alpha channel)
 */
export type LiveQuestAssetKind =
  | 'icon'
  | 'onboarding_icon'
  | 'plus_days'
  | 'sticker_pack'
  | 'chat_theme'
  | 'profile_background'
  | 'profile_background_animated'
  | 'profile_frame'
  | 'profile_frame_animated'
  | 'entry_animation'
  | 'season_badge'
  | 'leaderboard_prize'
  | 'guide_video'
  | 'hero';

export type LiveQuestMediaType =
  | 'image'
  | 'thumbnail'
  | 'webm'
  | 'ios_alpha'
  | 'theme_dark_mode'
  | 'thumbnail_dark_mode';

export type LiveQuestMediaSlot = {
  type: LiveQuestMediaType;
  label: string;
  required: boolean;
  accept: string;
  maxBytes: number;
  hint: string;
};

export type LiveQuestAssetProfile = {
  kind: LiveQuestAssetKind;
  title: string;
  slots: LiveQuestMediaSlot[];
  /** Plain-language requirements shown on the card. */
  requirements: string[];
  /** Who to ask, shown on the card. */
  contact?: string;
  /** In-console page where the rest of the asset is managed. */
  managePath?: string;
};

const MB = 1024 * 1024;

const imageSlot = (required: boolean, maxBytes: number, hint: string, label = 'Reward image'): LiveQuestMediaSlot => ({
  type: 'image',
  label,
  required,
  accept: 'image/png,image/jpeg,image/webp',
  maxBytes,
  hint,
});

const webmSlot = (required: boolean, maxBytes = 15 * MB): LiveQuestMediaSlot => ({
  type: 'webm',
  label: 'Android (.webm)',
  required,
  accept: 'video/webm,.webm',
  maxBytes,
  hint: `.webm with alpha transparency, ${maxBytes / MB} MB max`,
});

const movSlot = (required: boolean, maxBytes = 15 * MB): LiveQuestMediaSlot => ({
  type: 'ios_alpha',
  label: 'iOS (.mov)',
  required,
  accept: 'video/quicktime,.mov',
  maxBytes,
  hint: `.mov with alpha transparency, ${maxBytes / MB} MB max`,
});

const thumbnailSlot = (required: boolean, hint: string, accept = 'image/webp', maxBytes = 1 * MB): LiveQuestMediaSlot => ({
  type: 'thumbnail',
  label: 'Thumbnail',
  required,
  accept,
  maxBytes,
  hint,
});

const PROFILES: Record<LiveQuestAssetKind, LiveQuestAssetProfile> = {
  onboarding_icon: {
    kind: 'onboarding_icon',
    title: 'Onboarding icon',
    slots: [imageSlot(true, 256 * 1024, 'WebP or PNG, 256 KB max; export at 128–256 px')],
    requirements: ['Use a small transparent image so it loads promptly on mobile data.'],
  },
  icon: {
    kind: 'icon',
    title: 'Currency or gift',
    slots: [imageSlot(false, 5 * MB, 'PNG, JPG or WebP, 5 MB max')],
    requirements: ['One image, shown on the reward card.'],
  },
  plus_days: {
    kind: 'plus_days',
    title: 'PingTop Plus days',
    slots: [imageSlot(false, 5 * MB, 'PNG, JPG or WebP, 5 MB max', 'Thumbnail image')],
    requirements: [
      'One thumbnail. The PingTop Plus thumbnail is already added.',
      'Claiming turns Plus on automatically; level 20 (free) gives 2 days, level 49 (Plus) adds 7 days to the current end date.',
    ],
  },
  sticker_pack: {
    kind: 'sticker_pack',
    title: 'Sticker pack',
    slots: [imageSlot(false, 5 * MB, 'PNG or WebP, 5 MB max', 'Pack cover')],
    requirements: [
      'The pack itself holds 5 to 10 stickers (PNG or WebP) that users can send in live chat and regular chats.',
      'Upload the pack with the sticker tool using the link below and keep "Live Quest reward only" ticked: the pack is free, has no price and is not listed in the Shop. This card holds the cover shown on the reward.',
    ],
    contact: 'Mirza manages sticker packs.',
    managePath: '/products/stickers/bulk',
  },
  chat_theme: {
    kind: 'chat_theme',
    title: 'Chat theme',
    slots: [
      imageSlot(true, 5 * MB, 'PNG, JPG or WebP, 5 MB max', 'Theme background'),
      thumbnailSlot(true, 'Preview shown on the reward, PNG or WebP, 1 MB max', 'image/png,image/webp'),
    ],
    requirements: [
      'Set independent sender and receiver colors (hex) and choose whether the theme looks the same in light and dark mode.',
      'Locked (default): one background, thumbnail and sender/receiver pair are used in both modes.',
      'Unlocked: also upload a dark-mode background and thumbnail, and set dark sender/receiver colors.',
    ],
    contact: 'Shah and Rishabh are preparing these assets.',
  },
  profile_background: {
    kind: 'profile_background',
    title: 'Profile background',
    slots: [imageSlot(true, 5 * MB, 'PNG, JPG or WebP, 5 MB max', 'Background image')],
    requirements: ['One image. The Premium (Plus) backgrounds are animated and need both videos.'],
  },
  profile_background_animated: {
    kind: 'profile_background_animated',
    title: 'Animated profile background',
    slots: [webmSlot(true), movSlot(true), thumbnailSlot(true, 'WebP, 1 MB max')],
    requirements: [
      'Android .webm and iOS .mov (15 MB max each) plus a WebP thumbnail (1 MB max).',
      'Same dimensions and timing in both videos, no unwanted opaque background, 2160 px max per side.',
      'Older app versions show the thumbnail instead of the animation.',
    ],
    contact: 'Shah manages animated backgrounds.',
  },
  profile_frame: {
    kind: 'profile_frame',
    title: 'Profile frame',
    slots: [
      imageSlot(true, 1 * MB, 'PNG, exactly 1024 x 1024, 1 MB max', 'Frame image'),
      webmSlot(false),
      movSlot(false),
    ],
    requirements: [
      'One PNG image, exactly 1024 x 1024 and max 1 MB.',
      'Optional animation: .webm for Android and .mov for iOS. Keep every file the same aspect ratio.',
    ],
    contact: 'Shah manages frame assets.',
    managePath: '/products/frames/bulk',
  },
  profile_frame_animated: {
    kind: 'profile_frame_animated',
    title: 'Animated profile frame',
    slots: [
      webmSlot(true),
      movSlot(true),
      thumbnailSlot(true, 'WebP, 1 MB max, same aspect ratio as the videos'),
    ],
    requirements: [
      'Android .webm and iOS .mov, both with alpha transparency, 15 MB max each.',
      'A WebP thumbnail, 1 MB max. All files the same aspect ratio (for example square), 1024 x 1024 max.',
      'Older app versions show the thumbnail instead of the animation.',
    ],
    contact: 'Shah manages frame assets.',
    managePath: '/products/frames/bulk?type=profile_frame_animated',
  },
  entry_animation: {
    kind: 'entry_animation',
    title: 'Live entry (join) animation',
    slots: [
      movSlot(true),
      webmSlot(true),
      thumbnailSlot(true, 'WebP, 1 MB max'),
    ],
    requirements: [
      'iOS .mov with alpha transparency, Android .webm and a thumbnail shown on the reward card.',
      'The thumbnail is required because reward lists cannot preview the platform video files.',
    ],
    contact: 'Shah manages entry animations.',
  },
  season_badge: {
    kind: 'season_badge',
    title: 'Season badge',
    slots: [imageSlot(true, 1 * MB, 'PNG or WebP with transparency, 1 MB max', 'Badge image')],
    requirements: ['One transparent badge image. It is owned and managed separately from the Stage 50 profile frame.'],
  },
  leaderboard_prize: {
    kind: 'leaderboard_prize',
    title: 'Leaderboard prize image',
    slots: [imageSlot(true, 2 * MB, 'PNG or WebP, 2 MB max', 'Prize image')],
    requirements: [
      'One transparent PNG or WebP shown on the matching Top 3 leaderboard prize card.',
      'This controls the card artwork only; leaderboard settlement and reward delivery are separate.',
    ],
    contact: 'Shah manages leaderboard prize artwork.',
  },
  guide_video: {
    kind: 'guide_video',
    title: 'Live Quest video',
    slots: [
      webmSlot(true),
      movSlot(true),
      thumbnailSlot(true, 'WebP, 1 MB max'),
    ],
    requirements: [
      'Android .webm and iOS .mov, 15 MB max each.',
      'A WebP thumbnail, 1 MB max, is shown while the video loads.',
      'Keep the same duration and visible crop on both platforms.',
    ],
  },
  hero: {
    kind: 'hero',
    title: 'Season hero crest',
    slots: [imageSlot(true, 5 * MB, 'PNG or WebP with transparency, 5 MB max', 'Crest artwork')],
    requirements: ['The crest shown in the Live Quest header on every tab.'],
  },
};

export const liveQuestAssetProfile = (kind: LiveQuestAssetKind): LiveQuestAssetProfile => PROFILES[kind];

export const liveQuestAssetKind = (reward: Pick<LiveQuestRewardSlot, 'id' | 'rewardType' | 'title'>): LiveQuestAssetKind => {
  switch (reward.rewardType) {
    case 'plusDuration':
      return 'plus_days';
    case 'stickerPack':
      return 'sticker_pack';
    case 'chatTheme':
      return 'chat_theme';
    case 'profileBackground':
      // The Premium (Plus) backgrounds are animated; the free ones are a still image.
      return reward.id.startsWith('plus') ? 'profile_background_animated' : 'profile_background';
    case 'entryAnimation':
      return 'entry_animation';
    case 'profileFrame':
      return /animated/i.test(reward.title) ? 'profile_frame_animated' : 'profile_frame';
    case 'seasonBadge':
      return 'season_badge';
    default:
      return 'icon';
  }
};

/** A reward is finished when every required slot has a file. */
export const missingRequiredSlots = (
  profile: LiveQuestAssetProfile,
  have: Partial<Record<string, unknown>>
): LiveQuestMediaSlot[] => profile.slots.filter((slot) => slot.required && !have[slot.type]);

const mb = (bytes: number) => `${(bytes / MB).toFixed(bytes < MB ? 2 : 0)} MB`;

/**
 * Checks one file against its slot before upload. Frames go through the same checks as the
 * product form (type, size, 1024 max, readable dimensions). Returns the measured size, if any.
 */
export const checkLiveQuestFile = async (
  kind: LiveQuestAssetKind,
  slot: LiveQuestMediaSlot,
  file: File
): Promise<{ Width?: number; Height?: number }> => {
  if (file.size > slot.maxBytes) {
    throw new Error(`${file.name} is ${mb(file.size)}; the limit for ${slot.label} is ${mb(slot.maxBytes)}.`);
  }
  if (kind === 'profile_frame' || kind === 'profile_frame_animated') {
    const size = await inspectProfileFrameFile(kind, slot.type, file);
    if (kind === 'profile_frame' && slot.type === 'image') {
      if (file.type !== 'image/png') throw new Error('Static frame must be a PNG image.');
      if (size.Width !== 1024 || size.Height !== 1024) throw new Error('Static frame must be exactly 1024 x 1024.');
    }
    return size;
  }
  if (kind === 'profile_background_animated') {
    return inspectAnimatedBackgroundFile(slot.type, file);
  }
  if (slot.type === 'webm' && file.type && file.type !== 'video/webm') throw new Error('Android file must be a .webm video.');
  if (slot.type === 'ios_alpha' && file.type && file.type !== 'video/quicktime') throw new Error('iOS file must be a .mov video.');
  if ((slot.type === 'image' || slot.type === 'thumbnail') && !file.type.startsWith('image/')) {
    throw new Error(`${slot.label} must be an image.`);
  }
  return {};
};

// ───────────────────────────── Chat theme appearance ─────────────────────────────

export type ChatThemeSettings = {
  /** Same look in light and dark mode: one background, one thumbnail and one sender/receiver pair. */
  lockAppearance: boolean;
  senderLight: string;
  receiverLight: string;
  senderDark: string;
  receiverDark: string;
};

export const DEFAULT_CHAT_THEME_SETTINGS: ChatThemeSettings = {
  lockAppearance: true,
  senderLight: '',
  receiverLight: '',
  senderDark: '',
  receiverDark: '',
};

export const isHexColor = (value: string) => /^#[0-9a-f]{6}$/i.test(value.trim());

/** Files the unlocked (separate dark mode) theme also needs. */
export const CHAT_THEME_DARK_SLOTS: LiveQuestMediaSlot[] = [
  {
    type: 'theme_dark_mode',
    label: 'Dark-mode background',
    required: true,
    accept: 'image/png,image/jpeg,image/webp',
    maxBytes: 5 * MB,
    hint: 'PNG, JPG or WebP, 5 MB max',
  },
  {
    type: 'thumbnail_dark_mode',
    label: 'Dark-mode thumbnail',
    required: true,
    accept: 'image/png,image/webp',
    maxBytes: 1 * MB,
    hint: 'PNG or WebP, 1 MB max',
  },
];

type Media = { Type: string; Url: string; MimeType?: string; Width?: number; Height?: number };

/**
 * The media the product must hold for a chat theme, in the slots the app's chat theme code reads
 * (theme / thumbnail / bubble, light and dark). Locked: the one background, thumbnail and bubble
 * color fill both modes. Unlocked: the dark files and dark color are used for dark mode. Returns the
 * given files plus the derived ones; any stale derived entries are replaced.
 */
export const deriveChatThemeMedia = (media: Media[], settings: ChatThemeSettings): Media[] => {
  const derived = new Set([
    'theme_light_mode',
    'theme_dark_mode',
    'thumbnail_light_mode',
    'thumbnail_dark_mode',
    'bubble_light_mode',
    'bubble_dark_mode',
    'sender_bubble_light_mode',
    'receiver_bubble_light_mode',
    'sender_bubble_dark_mode',
    'receiver_bubble_dark_mode',
  ]);
  const own = media.filter((m) => !derived.has(m.Type) || (!settings.lockAppearance && (m.Type === 'theme_dark_mode' || m.Type === 'thumbnail_dark_mode')));
  const find = (type: string) => own.find((m) => m.Type === type);
  const image = find('image');
  const thumb = find('thumbnail');
  const out = own.filter((m) => m.Type !== 'theme_dark_mode' && m.Type !== 'thumbnail_dark_mode');
  const push = (Type: string, source?: Media) => {
    if (source) out.push({ ...source, Type });
  };
  push('theme_light_mode', image);
  push('thumbnail_light_mode', thumb);
  push('theme_dark_mode', settings.lockAppearance ? image : find('theme_dark_mode'));
  push('thumbnail_dark_mode', settings.lockAppearance ? thumb : find('thumbnail_dark_mode'));
  const senderLight = settings.senderLight.trim();
  const receiverLight = settings.receiverLight.trim();
  const senderDark = settings.lockAppearance ? senderLight : settings.senderDark.trim();
  const receiverDark = settings.lockAppearance ? receiverLight : settings.receiverDark.trim();
  // Keep the older single-bubble fields populated with the sender color while new clients read the
  // explicit sender/receiver fields.
  if (isHexColor(senderLight)) {
    out.push({ Type: 'bubble_light_mode', Url: senderLight });
    out.push({ Type: 'sender_bubble_light_mode', Url: senderLight });
  }
  if (isHexColor(receiverLight)) out.push({ Type: 'receiver_bubble_light_mode', Url: receiverLight });
  if (isHexColor(senderDark)) {
    out.push({ Type: 'bubble_dark_mode', Url: senderDark });
    out.push({ Type: 'sender_bubble_dark_mode', Url: senderDark });
  }
  if (isHexColor(receiverDark)) out.push({ Type: 'receiver_bubble_dark_mode', Url: receiverDark });
  return out;
};

/** Everything a chat theme product may hold. */
export const CHAT_THEME_MEDIA_TYPES = [
  'image',
  'thumbnail',
  'theme_light_mode',
  'theme_dark_mode',
  'thumbnail_light_mode',
  'thumbnail_dark_mode',
  'bubble_light_mode',
  'bubble_dark_mode',
  'sender_bubble_light_mode',
  'receiver_bubble_light_mode',
  'sender_bubble_dark_mode',
  'receiver_bubble_dark_mode',
];

/**
 * The media types a reward of this kind may carry. Anything else attached earlier (for example the
 * picture of the reward that used to sit at this level) is dropped on the next save, so it can no
 * longer be shown instead of the right artwork.
 */
export const allowedMediaTypes = (kind: LiveQuestAssetKind): Set<string> =>
  kind === 'chat_theme'
    ? new Set(CHAT_THEME_MEDIA_TYPES)
    : new Set(PROFILES[kind].slots.map((slot) => slot.type as string));

// ───────────────────────────── Product class ─────────────────────────────

export type LiveQuestProductClass = { CategoryKey: string; Type: string };

/**
 * The wallet product type and category a reward is saved with. Item rewards (themes, backgrounds,
 * frames, entry animations) are real typed products, inactive so they are never sold, so the app
 * and the wallet treat what a user is given as the real thing. Everything else (coins, tokens, Plus
 * days, sticker pack covers, the hero crest) stays a LIVE_QUEST utility record that only holds artwork.
 */
export const liveQuestProductClass = (kind: LiveQuestAssetKind): LiveQuestProductClass => {
  switch (kind) {
    case 'chat_theme':
      return { CategoryKey: 'PROFILE', Type: 'chat_themes' };
    case 'profile_background':
      return { CategoryKey: 'PROFILE', Type: 'background' };
    case 'profile_background_animated':
      return { CategoryKey: 'PROFILE', Type: 'background_animated' };
    case 'profile_frame':
      return { CategoryKey: 'PROFILE', Type: 'profile_frame' };
    case 'profile_frame_animated':
      return { CategoryKey: 'PROFILE', Type: 'profile_frame_animated' };
    case 'entry_animation':
      return { CategoryKey: 'LIVE', Type: 'entry_animation' };
    case 'season_badge':
      return { CategoryKey: 'PROFILE', Type: 'profile_badge' };
    default:
      return { CategoryKey: 'LIVE_QUEST', Type: 'utility' };
  }
};

/**
 * The wallet validates multi-file animated profile products atomically. Keep an incomplete upload
 * as an inactive Live Quest utility draft, then promote it automatically when its final required
 * file is attached. Operators can therefore upload each platform file separately without making
 * an incomplete background or frame available to the app.
 */
export const liveQuestProductClassForMedia = (
  kind: LiveQuestAssetKind,
  media: Array<{ Type: string }>
): LiveQuestProductClass => {
  if (kind === 'profile_background_animated' || kind === 'profile_frame_animated') {
    const have = media.reduce<Partial<Record<string, true>>>((slots, item) => {
      slots[item.Type] = true;
      return slots;
    }, {});
    if (missingRequiredSlots(liveQuestAssetProfile(kind), have).length > 0) {
      return { CategoryKey: 'LIVE_QUEST', Type: 'utility' };
    }
  }
  return liveQuestProductClass(kind);
};
